import { useEffect, useRef, useState } from "react";
import { T, F } from "@/lib/bricks";
import BricksLoader from "@/components/BricksLoader";
import { openPdf, PdfPages, FIELD_SIZE, RECIPIENT_COLORS, useContainerWidth } from "./pdfPages";

const TYPES = [["signature", "Signature"], ["initial", "Initials"], ["date", "Date signed"], ["text", "Text"]];
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * Place signature, initials, date and text fields on the attached PDF.
 * Fields are { label, type, recipient_email, page, x, y, w, h, placed: true, required } with
 * x/y/w/h as fractions of the displayed page (top-left origin), the box the server stamps into.
 */
export default function FieldPlacer({ file, recipients, fields, onChange, p }) {
  const [pdf, setPdf] = useState(null);
  const [err, setErr] = useState(null);
  const [type, setType] = useState("signature");
  const [who, setWho] = useState(recipients[0]?.email || "");
  const [textLabel, setTextLabel] = useState("");
  const [wrapRef, width] = useContainerWidth(560);
  const drag = useRef(null);

  useEffect(() => {
    let live = true;
    setPdf(null); setErr(null);
    openPdf(file).then((d) => { if (live) setPdf(d); }).catch(() => { if (live) setErr("This PDF could not be opened for placement."); });
    return () => { live = false; };
  }, [file]);

  useEffect(() => {
    if (!recipients.some((r) => r.email === who)) setWho(recipients[0]?.email || "");
  }, [recipients, who]);

  const colorOf = (email) => RECIPIENT_COLORS[Math.max(0, recipients.findIndex((r) => r.email === email)) % RECIPIENT_COLORS.length];
  const nameOf = (email) => recipients.find((r) => r.email === email)?.name || email;

  const place = (page, fx, fy) => {
    if (!who) return;
    const { w, h } = FIELD_SIZE[type];
    const label = type === "text" ? (textLabel.trim() || "Text") : TYPES.find(([k]) => k === type)[1];
    onChange([...fields, {
      label, type, recipient_email: who, page,
      x: clamp(fx - w / 2, 0, 1 - w), y: clamp(fy - h / 2, 0, 1 - h), w, h,
      placed: true, required: true,
    }]);
  };

  const startDrag = (e, i, mode) => {
    e.stopPropagation(); e.preventDefault();
    const pageEl = e.currentTarget.closest("[data-page]");
    if (!pageEl) return;
    const r = pageEl.getBoundingClientRect();
    drag.current = { i, mode, r, sx: e.clientX, sy: e.clientY, f0: { ...fields[i] } };
    const move = (ev) => {
      const d = drag.current; if (!d) return;
      const dx = (ev.clientX - d.sx) / d.r.width, dy = (ev.clientY - d.sy) / d.r.height;
      const f = { ...d.f0 };
      if (d.mode === "move") {
        f.x = clamp(d.f0.x + dx, 0, 1 - f.w); f.y = clamp(d.f0.y + dy, 0, 1 - f.h);
      } else {
        f.w = clamp(d.f0.w + dx, 0.03, 1 - f.x); f.h = clamp(d.f0.h + dy, 0.015, 1 - f.y);
      }
      onChangeRef.current(fieldsRef.current.map((x, k) => (k === d.i ? f : x)));
    };
    const up = () => { drag.current = null; window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const fieldsRef = useRef(fields); fieldsRef.current = fields;
  const onChangeRef = useRef(onChange); onChangeRef.current = onChange;

  const remove = (i) => onChange(fields.filter((_, k) => k !== i));
  const toggleRequired = (i) => onChange(fields.map((f, k) => (k === i ? { ...f, required: !f.required } : f)));

  const chip = (active) => ({
    padding: "6px 10px", borderRadius: 2, fontSize: 12, fontFamily: F.display, cursor: "pointer",
    border: `1px solid ${active ? T.brick : p.hairS}`, background: active ? T.brickTint : "transparent", color: active ? T.brickDeep : p.ink2,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <select value={who} onChange={(e) => setWho(e.target.value)} aria-label="Recipient"
          style={{ padding: "7px 10px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 12, fontFamily: F.display, color: p.ink, background: p.bg, outline: "none", borderLeft: `4px solid ${colorOf(who)}` }}>
          {recipients.map((r) => <option key={r.email} value={r.email}>{r.name || r.email}</option>)}
        </select>
        {TYPES.map(([k, label]) => <button key={k} type="button" onClick={() => setType(k)} style={chip(type === k)}>{label}</button>)}
        {type === "text" && (
          <input value={textLabel} onChange={(e) => setTextLabel(e.target.value)} placeholder="Label, e.g. Irrevocable date"
            style={{ flex: "1 1 160px", padding: "7px 10px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 12, fontFamily: F.display, color: p.ink, background: "transparent", outline: "none" }} />
        )}
      </div>
      <div style={{ fontSize: 12, color: p.ink3 }}>
        Click the document to drop a {TYPES.find(([k]) => k === type)[1].toLowerCase()} field for {nameOf(who) || "the recipient"}. Drag to move, pull the corner to resize. Values are stamped onto the signed PDF.
      </div>
      <div ref={wrapRef} style={{ maxHeight: "58vh", overflowY: "auto", background: p.surf2, padding: 12, borderRadius: 2, border: `1px solid ${p.hair}` }}>
        {err && <div style={{ fontSize: 13, color: T.bad, padding: 12 }}>{err}</div>}
        {!pdf && !err && <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><BricksLoader variant="assemble" size={32} color={T.brick} /></div>}
        {pdf && (
          <PdfPages pdf={pdf} width={Math.max(240, width - 24)} onPageClick={place}
            renderOverlay={(pageNo, size) => fields.map((f, i) => (f.placed && f.page === pageNo ? (
              <div key={i} data-field onPointerDown={(e) => startDrag(e, i, "move")}
                title={`${f.label} · ${nameOf(f.recipient_email)}`}
                style={{
                  position: "absolute", left: f.x * size.w, top: f.y * size.h, width: f.w * size.w, height: f.h * size.h,
                  border: `1.5px solid ${colorOf(f.recipient_email)}`, background: `${colorOf(f.recipient_email)}1F`, borderRadius: 2,
                  cursor: "move", boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 4px", overflow: "hidden", touchAction: "none",
                }}>
                <span style={{ fontSize: Math.max(9, Math.min(12, f.h * size.h * 0.45)), color: colorOf(f.recipient_email), fontFamily: F.display, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
                  {f.label}{f.type === "text" && !f.required ? " (optional)" : ""}
                </span>
                <button type="button" aria-label="Remove field" onPointerDown={(e) => e.stopPropagation()} onClick={() => remove(i)}
                  style={{ border: "none", background: "transparent", color: colorOf(f.recipient_email), cursor: "pointer", fontSize: 13, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
                <span onPointerDown={(e) => startDrag(e, i, "resize")}
                  style={{ position: "absolute", right: -1, bottom: -1, width: 9, height: 9, background: colorOf(f.recipient_email), cursor: "nwse-resize" }} />
              </div>
            ) : null))}
          />
        )}
      </div>
      {fields.some((f) => f.placed) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {fields.map((f, i) => f.placed ? (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: p.ink2 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: colorOf(f.recipient_email), flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{f.label} · page {f.page} · {nameOf(f.recipient_email)}</span>
              {f.type === "text" && (
                <button type="button" onClick={() => toggleRequired(i)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: T.brick, fontFamily: F.display }}>
                  {f.required ? "Required" : "Optional"}
                </button>
              )}
            </div>
          ) : null)}
        </div>
      )}
    </div>
  );
}
